import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import listingService from '@/shared/services/listingService';
import { serverMessage } from '@/shared/utils/serverMessage';

// Matches QUESTION_MAX in server/src/api/listing/listingQuestion.validation.js.
export const QUESTION_MAX = 1000;

const SENT =
    'Your question was sent to the poster. Their answer comes to your email.';
const LIMIT_REACHED =
    'You have asked as many questions about this listing as one day allows. Try again tomorrow, or email the poster directly.';
const FAILED = 'Could not send your question. Try again.';

/**
 * "Ask the poster a question": a short form whose text is emailed to the
 * owner with Reply-To set to the asker, so the answer arrives as an ordinary
 * reply and the exchange keeps no thread. The page decides who sees it (a
 * signed-in member who is not the owner, on an open listing); the server
 * enforces the same and the daily limit.
 */
function AskQuestion({ listingId }) {
    const [open, setOpen] = useState(false);
    const [question, setQuestion] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);
    const openerRef = useRef(null);
    const fieldRef = useRef(null);
    const wasOpen = useRef(false);

    // Focus follows the form: into the field when it opens, back to the
    // button that opened it when it closes, so a keyboard user is never
    // dropped to the top of the page.
    useEffect(() => {
        if (open) fieldRef.current?.focus();
        else if (wasOpen.current) openerRef.current?.focus();
        wasOpen.current = open;
    }, [open]);

    const close = () => {
        setOpen(false);
        setQuestion('');
        setError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSending(true);
        setError('');
        try {
            await listingService.askQuestion(listingId, question.trim());
            setSent(true);
            close();
        } catch (caught) {
            setError(
                caught?.response?.status === 429
                    ? LIMIT_REACHED
                    : serverMessage(caught, FAILED)
            );
        } finally {
            setSending(false);
        }
    };

    const blank = question.trim().length === 0;

    return (
        <section className="mt-2" aria-labelledby="ask-question-heading">
            <h2 id="ask-question-heading" className="visually-hidden">
                Ask the poster a question
            </h2>
            {/* Present before any message, so a screen reader that watches
                it announces the confirmation when it appears. A plain styled
                div rather than an Alert, whose role="alert" would turn a
                polite confirmation into an interruption. */}
            <div role="status">
                {sent && <div className="alert alert-success">{SENT}</div>}
            </div>
            {!open && (
                <Button
                    ref={openerRef}
                    variant="outline-primary"
                    className="w-100"
                    onClick={() => {
                        setSent(false);
                        setOpen(true);
                    }}
                >
                    Ask the poster a question
                </Button>
            )}
            {open && (
                <Form
                    onSubmit={handleSubmit}
                    aria-labelledby="ask-question-heading"
                >
                    {error && (
                        <Alert variant="danger" role="alert">
                            {error}
                        </Alert>
                    )}
                    <Form.Group className="mb-2" controlId="listing-question">
                        <Form.Label>Your question</Form.Label>
                        <Form.Control
                            ref={fieldRef}
                            as="textarea"
                            rows={4}
                            name="question"
                            value={question}
                            maxLength={QUESTION_MAX}
                            onChange={(event) =>
                                setQuestion(event.target.value)
                            }
                            aria-describedby="listing-question-count"
                            required
                        />
                        <Form.Text id="listing-question-count" muted>
                            {question.length} of {QUESTION_MAX} characters. The
                            poster replies to your email address.
                        </Form.Text>
                    </Form.Group>
                    <div className="d-flex gap-2">
                        <Button type="submit" disabled={blank || sending}>
                            {sending ? 'Sending...' : 'Send'}
                        </Button>
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={close}
                        >
                            Cancel
                        </Button>
                    </div>
                </Form>
            )}
        </section>
    );
}

export default AskQuestion;
